<template>
  <v-form ref="form" v-model="formIsValid" style="width: 100%; padding-top: 0px;">
    <v-card-text>
      <ExecutionResultsDialog title="Move Data Source" @close="close()" :shouldClose="!assignOptions.isTestRun" ref="executionResultsDialog" />

      <v-autocomplete v-model="formObject.dataSourceToMerge" :items="selectionItems" :rules="[rules.required, rules.noSynonyms]" required item-text="Name" item-value="Id" return-object label="Discrete data source to merge" />

      <template v-if="isDepthHeightDatum">
        <v-layout row wrap>
          <v-flex xs12 md8>
            <v-autocomplete :items="datumList" :rules="[rules.required]" required item-text="DatumName" v-model="targetDatum" label="Depth/Height Datum" return-object />
          </v-flex>
          <v-flex xs12 offset-md1 md3>
            <v-text-field v-if="targetDatum && targetDatum.DatumType" disabled v-model="targetDatum.DatumType.Name" label="Datum Type" />
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12 sm6 md5>
            <v-text-field label="Start value (meters)" type="number" v-model.number="formObject.depthHeightStartValue" :rules="[rules.depthHeightRules, rules.requiredNumber]" />
          </v-flex>
          <v-spacer />
          <v-flex xs12 sm6 md5>
            <v-text-field label="End value (meters)" type="number" v-model.number="formObject.depthHeightEndValue" :rules="[rules.depthHeightRules, rules.requiredNumber]" />
          </v-flex>
        </v-layout>
      </template>

      <v-checkbox color="error" v-model="assignOptions.isTestRun" label="Test Run" persistent-hint hint="See logs of the outcome without making any changes" />

      <template v-if="isSystemAdmin">
        <v-subheader class="pl-0 mt-2">System Administrator Settings</v-subheader>
        <v-checkbox class="mt-0" color="info" v-model="assignOptions.makePrimary" label="Make primary name" persistent-hint hint="New synonym will become the primary name" />
      </template>
    </v-card-text>
    <v-alert key="alert" outline class="my-0" type="warning" :value="assignOptions.makePrimary">
      <div class="primary--text">
        The primary alias name shows up in reports, which may cause confusion when the primary alias does not match
        thelocation name.
      </div>
    </v-alert>
  </v-form>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const authenticate = createNamespacedHelpers('authenticate')
  const datums = createNamespacedHelpers('datum')
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')
  const navigation = createNamespacedHelpers('navigation')

  import {
    hasNonPrimarySynonyms,
  } from '@/helpers/synonyms'

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  export default {
    props: {
      isQaQc: {
        type: Boolean,
        default: false,
      },
      isDepthHeightDatum: {
        type: Boolean,
        default: false,
      },
      givenStation: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        assignOptions: {
          isTestRun: true,

          // Defaults, but are not used.
          canCompromise: false,
          shouldMovePrimaryAlias: true,
          makePrimary: false,
        },
        formObject: {
          dataSourceToMerge: null,
          depthHeightStartValue: null,
          depthHeightEndValue: null,
        },
        targetDatum: {},
        numRecordsWithoutDepthHeight: null,
        loading: false,
        rules: {
          depthHeightRules: value => {
            if (value < 0) return 'Value must be positive'
            if (isNaN(value)) return 'Value must be a number/decimal'
            if (this.formObject.depthHeightStartValue &&
              this.formObject.depthHeightEndValue &&
              this.formObject.depthHeightStartValue > this.formObject.depthHeightEndValue) {
              return 'Start value must be less than end value'
            }

            return true
          },
          required: value => !!value || 'Required',
          requiredNumber: value => (!!value || value === 0) || 'Required',
          noSynonyms: value => {
            return !value || (value && !value.hasSynonyms ||
              'Data source has non-primary aliases. Please separate its aliases and merge them individually.')
          },
        },
        formIsValid: true,
      }
    },
    computed: {
      ...authenticate.mapGetters(['isSystemAdmin']),
      ...datums.mapGetters(['datumList']),
      ...monitoringFeatures.mapGetters(['currentMonitoringFeature']),
      ...dataSources.mapGetters(['dataSources']),
      discreteStations() {
        return this.dataSources[SOURCE_TYPES.discreteEnvirodata]
      },
      givenStationId() {
        return this.givenStation && this.givenStation.Id
      },
      filteredDiscreteStations() {
        return this.discreteStations.filter(ds => ds.Id != this.givenStationId)
      },
      selectionItems() {
        return this.filteredDiscreteStations.map(station => ({
          Name: station.PrimaryName,
          Id: station.Id,
          hasSynonyms: hasNonPrimarySynonyms(SOURCE_TYPES.discreteEnvirodata, station),
        }))
      },
    },
    watch: {
      dataSourceToMerge(value) {
        if (this.dataSourceToMerge && this.isDepthHeightDatum) {
          this.getTargetWaterQualityStationDetails(value.Name)
        }
      },
      formIsValid(value) {
        this.$emit('update:valid', value)
      },
    },
    methods: {
      ...monitoringFeatures.mapActions(['refreshCurrentMFDetails', 'fetchMonitoringFeaturesUI']),
      ...dataSources.mapActions(['setDSMonitoringFeatureId', 'fetchDataSources']),
      ...navigation.mapActions(['fetchSideMenu']),
      async getTargetWaterQualityStationDetails(stationName) {
        await this.$store.dispatch('stations/getAndReturnWaterQualityStationDetailByName', {
          name: stationName,
          successCb: (stationDetail) => {
            if (stationDetail && stationDetail.items && stationDetail.items.Datasets) {
              let datasets = stationDetail.items.Datasets
              let totalRecords = datasets.map(x => x.NumberOfRecords).reduce((a, b) => a + b, 0)
              let totalRecordsWithDepthHeight = datasets.map(x => x.NumberOfRecordsWithDepthHeight).reduce((a,
                b) => a + b, 0)

              this.numRecordsWithoutDepthHeight = totalRecords - totalRecordsWithDepthHeight
            }
          },
        })
      },
      close() {
        this.$emit('close')
      },
      async submit() {
        if (!this.$refs.form.validate()) return
        this.loading = true

        try {
          let payload = {
            sourceType: SOURCE_TYPES.discreteEnvirodata,
            sourceId: this.formObject.dataSourceToMerge.Id,
            monitoringFeatureId: this.currentMonitoringFeature.Id,
            canCompromise: this.assignOptions.canCompromise,
            shouldMovePrimaryAlias: this.assignOptions.shouldMovePrimaryAlias,
            isTestRun: this.assignOptions.isTestRun,
            makePrimary: this.assignOptions.makePrimary,
            baseSynonym: {
              synonymData: {
                depthHeightDatumId: this.targetDatum && this.targetDatum.Id,
                isQaQc: this.isQaQc,
                isDepthHeightDatum: this.isDepthHeightDatum,
                depthHeightStartValue: this.formObject.depthHeightStartValue,
                depthHeightEndValue: this.formObject.depthHeightEndValue,
              },
            },
          }

          let executionResults = await this.setDSMonitoringFeatureId(payload)
          this.$refs.executionResultsDialog.show(executionResults)

          let fetchDataSourcesPromise = this.fetchDataSources({
            sourceTypes: [SOURCE_TYPES.discreteEnvirodata],
          })

          let refreshCurrentMFPromise = this.refreshCurrentMFDetails({
            sourceTypes: [SOURCE_TYPES.discreteEnvirodata],
          })

          let fetchAllMFsPromise = this.fetchMonitoringFeaturesUI()
          let fetchSideMenuPromise = this.fetchSideMenu()

          await Promise.all([fetchDataSourcesPromise, refreshCurrentMFPromise, fetchAllMFsPromise, fetchSideMenuPromise])
        } catch (error) {
          console.error(error)
          this.$refs.executionResultsDialog.show(error)
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>